import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../../../api';
import Topbar from '../../Tools/Topbar';
import LeftSidebar from '../../Tools/LeftSidebar';
import Settings from '../../Tools/Setting/Settings';
import useSettings from '../../Tools/Setting/useSettings';
import { format } from 'date-fns';

// Date/Time Format Fetcher
const DateTimeFormatFetcher = () => {
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axiosInstance.get('/ems/get-system-setting', {
          headers: {
            Authorization: `Bearer ${sessionStorage.getItem('authToken')}`,
          },
        });
        if (res.data?.status === 'success' && res.data.data.length > 0) {
          const data = res.data.data[0];
          sessionStorage.setItem('date_format', data.date_format || 'DD/MM/YYYY');
          sessionStorage.setItem('time_format', data.time_format || '24');
        }
      } catch (err) {
        console.error('Error fetching date/time formats:', err);
        sessionStorage.setItem('date_format', 'DD/MM/YYYY');
        sessionStorage.setItem('time_format', '24');
      }
    };
    fetchSettings();
  }, []);
  return null;
};

const RejectsCaseDetailsTab = () => {
  const navigate = useNavigate();

  const [caseId] = useState(sessionStorage.getItem('currentCaseId'));
  const caseStatus = sessionStorage.getItem('caseStatus') || '1';
  const [caseData, setCaseData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [userRole] = useState(sessionStorage.getItem('Role_Name') || '');

  const settingsRef = useRef();
  const { settings, updateSetting, handleColorTheme } = useSettings();

  const toggleSidebar = () => setIsSidebarOpen(prev => !prev);

  // Format date according to system settings (UTC-safe)
  const formatCaseDate = (dateString) => {
    if (!dateString) return 'N/A';
  
    try {
      const dateFormat = sessionStorage.getItem('date_format') || 'DD/MM/YYYY';
      const timeFormat = sessionStorage.getItem('time_format') || '24';
  
      // Map your backend formats to date-fns
      const dateFormatMap = {
        'DD/MM/YYYY': 'dd/MM/yyyy',
        'MM/DD/YYYY': 'MM/dd/yyyy',
        'YYYY-MM-DD': 'yyyy-MM-dd',
      };
  
      const timeFormatString = timeFormat === '12' ? 'hh:mm:ss a' : 'HH:mm:ss';
      const formatString = `${dateFormatMap[dateFormat] || 'dd/MM/yyyy'} ${timeFormatString}`;
  
      let utcDate;
  
      if (dateString.includes('T')) {
        // ISO format: 2025-10-27T16:27:19.000000Z
        utcDate = new Date(dateString.endsWith('Z') ? dateString : dateString + 'Z');
      } else {
        // MySQL format: 2025-10-27 16:27:19
        utcDate = new Date(dateString.replace(' ', 'T') + 'Z'); // Force UTC
      }
  
      // Extract UTC parts manually to avoid local timezone shift
      const formatted = format(
        new Date(
          utcDate.getUTCFullYear(),
          utcDate.getUTCMonth(),
          utcDate.getUTCDate(),
          utcDate.getUTCHours(),
          utcDate.getUTCMinutes(),
          utcDate.getUTCSeconds()
        ),
        formatString
      );
  
      return formatted;
    } catch (err) {
      console.error('Error formatting alarm time:', err, dateString);
      return 'Invalid Date';
    }
  };

  // Fetch case details
  useEffect(() => {
    const fetchCaseDetails = async () => {
      if (!caseId) {
        setError('No case selected');
        setIsLoading(false);
        return;
      }

      if (!sessionStorage.getItem('auth_token')) {
        setError('Authentication required');
        setIsLoading(false);
        navigate('/login');
        return;
      }

      try {
        setIsLoading(true);
        const response = await axiosInstance.get(`/ems/cases/${caseId}`);
        setCaseData(response.data.data || response.data);
        setError(null);
      } catch (err) {
        console.warn('API failed, trying mock data...', err);
        // Optional: keep mock data fallback here if needed
        setError('Failed to load case details');
      } finally {
        setIsLoading(false);
      }
    };

    fetchCaseDetails();
  }, [caseId, navigate]);

  // Document handling
  const getDocuments = () => {
    if (!caseData) return [];
    return [
      caseData.documents1,
      caseData.documents2,
      caseData.documents3,
    ].filter(doc => doc && doc.trim() !== '');
  };

  const handleDocumentClick = (url) => {
    setSelectedDocument(url);
    setShowDocumentModal(true);
  };

  const renderDocumentContent = (url) => {
    const ext = url.split('.').pop().toLowerCase();
    if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) {
      return <img src={url} alt="Document" className="img-fluid" style={{ maxHeight: '80vh' }} />;
    }
    if (['pdf', 'xml'].includes(ext)) {
      return <iframe src={url} width="100%" height="600px" title="Document" />;
    }
    return <p>Unsupported file type: {ext}</p>;
  };

  const documents = getDocuments();

  return (
    <div id="main-wrapper">
      <DateTimeFormatFetcher />

      {/* Always render layout — this keeps Topbar & hamburger working */}
      <LeftSidebar isSidebarOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />
      <div className={`page-wrapper ${isSidebarOpen ? '' : 'sidebar-closed'}`}>
        <Topbar toggleSidebar={toggleSidebar} />

        <div className="body-wrapper">
          <div className="container-fluid">
            <Settings
              ref={settingsRef}
              updateSetting={updateSetting}
              handleColorTheme={handleColorTheme}
              settings={settings}
            />

            {/* Show loading spinner inside content area */}
            {isLoading && (
              <div className="d-flex justify-content-center align-items-center py-5 my-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading case...</span>
                </div>
              </div>
            )}

            {error && !isLoading && (
              <div className="alert alert-danger m-4">{error}</div>
            )}

            {!isLoading && !error && !caseData && (
              <div className="alert alert-warning m-4">No case data found</div>
            )}

            {!isLoading && caseData && (
              <div className="card w-100 position-relative overflow-hidden">
                <div className="px-4 py-3 border-bottom">
                  <h5 className="card-title fw-semibold mb-0">Disapproved Case Details</h5>
                  <p className="card-subtitle mb-0 mt-1">
                    Comprehensive overview of a disapproved case
                  </p>
                </div>

                <div className="card-body p-4">
                  <div className="table-responsive rounded-2">
                    <table className="table text-nowrap customize-table mb-0 align-middle">
                      <thead className="text-dark fs-4">
                        <tr>
                          <th>
                            <h6 className="fw-semibold mb-0">Field</h6>
                          </th>
                          <th>
                            <h6 className="fw-semibold mb-0">Value</h6>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>
                            <p className="mb-0 fw-normal fw-semibold">Case ID</p>
                          </td>
                          <td>
                            <p className="mb-0 fw-normal">{caseData.id || 'N/A'}</p>
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <p className="mb-0 fw-normal fw-semibold">Name</p>
                          </td>
                          <td>
                            <p className="mb-0 fw-normal">{caseData.case_name || 'N/A'}</p>
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <p className="mb-0 fw-normal fw-semibold">Location</p>
                          </td>
                          <td>
                            <p className="mb-0 fw-normal">{caseData.location?.location_name || 'N/A'}</p>
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <p className="mb-0 fw-normal fw-semibold">Ambulance</p>
                          </td>
                          <td>
                            <p className="mb-0 fw-normal">{caseData.ambulance?.plate_number || 'N/A'}</p>
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <p className="mb-0 fw-normal fw-semibold">Medical Center</p>
                          </td>
                          <td>
                            <p className="mb-0 fw-normal">{caseData.medical_center?.name || caseData.from_medical_center?.name || 'N/A'}</p>
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <p className="mb-0 fw-normal fw-semibold">Paramedics ID</p>
                          </td>
                          <td>
                            <p className="mb-0 fw-normal">{caseData.ems_member || 'N/A'}</p>
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <p className="mb-0 fw-normal fw-semibold">Paramedics Name</p>
                          </td>
                          <td>
                            <p className="mb-0 fw-normal">{caseData.ems_member_name || 'N/A'}</p>
                          </td>
                        </tr>
                          {caseStatus === '1' && (
                          <>
                            <tr>
                              <td><p className="mb-0 fw-normal fw-semibold mb-0">Patient Location</p></td>
                              <td><p className="mb-0 fw-normal">
                                {caseData?.p_location?.location_name || 'N/A'}
                              </p></td>
                            </tr>
                            <tr>
                              <td><p className="mb-0 fw-normal fw-semibold mb-0">Patient Area</p></td>
                              <td><p className="mb-0 fw-normal">
                                {caseData?.p_area?.area_name || 'N/A'}
                              </p></td>
                            </tr>
                          </>
                        )}

                        <tr>
                          <td>
                            <p className="mb-0 fw-normal fw-semibold">Gender</p>
                          </td>
                          <td>
                            <p className="mb-0 fw-normal">{caseData.gender || 'N/A'}</p>
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <p className="mb-0 fw-normal fw-semibold">Age</p>
                          </td>
                          <td>
                            <p className="mb-0 fw-normal">{caseData.age || 'N/A'}</p>
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <p className="mb-0 fw-normal fw-semibold">Bracelet ID</p>
                          </td>
                          <td>
                            <p className="mb-0 fw-normal">{caseData.bracelet_id || 'N/A'}</p>
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <p className="mb-0 fw-normal fw-semibold">Type of Injury</p>
                          </td>
                          <td>
                            <p className="mb-0 fw-normal">{caseData.injury_type || 'N/A'}</p>
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <p className="mb-0 fw-normal fw-semibold">Contact Number</p>
                          </td>
                          <td>
                            <p className="mb-0 fw-normal">{caseData.contact_number || 'N/A'}</p>
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <p className="mb-0 fw-normal fw-semibold">Triage Card</p>
                          </td>
                          <td>
                            <p className="mb-0 fw-normal">{caseData.triage_card || 'N/A'}</p>
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <p className="mb-0 fw-normal fw-semibold">Disapproval Notes</p>
                          </td>
                          <td>
                            <p className="mb-0 fw-normal">{caseData.disapproval_notes || 'N/A'}</p>
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <p className="mb-0 fw-normal fw-semibold">Disapproved At</p>
                          </td>
                          <td>
                            <p className="mb-0 fw-normal">{caseData.updated_at ? formatCaseDate(caseData.updated_at) : 'N/A'}</p>
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <p className="mb-0 fw-normal fw-semibold">Documents</p>
                          </td>
                          <td>
                            <p className="mb-0 fw-normal">
                              <i className="ti ti-paperclip me-2"></i>
                              {documents.length > 0 ? (
                                documents.map((doc, index) => (
                                  <button
                                    key={index}
                                    type="button"
                                    className="btn btn-link me-3 p-0"
                                    onClick={() => handleDocumentClick(doc)}
                                    title={`Document ${index + 1}`}
                                  >
                                    <i className="ti ti-library-photo"></i>
                                  </button>
                                ))
                              ) : (
                                'No documents'
                              )}
                            </p>
                          </td>
                        </tr>
                        <tr>
                          <td>
                            <p className="mb-0 fw-normal fw-semibold">Case Date</p>
                          </td>
                          <td>
                            <p className="mb-0 fw-normal">{caseData.created_at ? formatCaseDate(caseData.created_at) : 'N/A'}</p>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Document Viewer Modal */}
            {showDocumentModal && (
              <div className="modal fade show d-block" style={{ background: 'rgba(0,0,0,0.5)' }}>
                <div className="modal-dialog modal-lg">
                  <div className="modal-content">
                    <div className="modal-header">
                      <h5 className="modal-title">Attachment</h5>
                      <button type="button" className="btn-close" onClick={() => setShowDocumentModal(false)}></button>
                    </div>
                    <div className="modal-body text-center">
                      {selectedDocument && renderDocumentContent(selectedDocument)}
                    </div>
                    <div className="modal-footer">
                      <button className="btn btn-danger" onClick={() => setShowDocumentModal(false)}>
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RejectsCaseDetailsTab;